import { faArrowUpFromBracket, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, ErrorModal, Flex, Input, InputProps, Text } from '@modusign/design-system';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useHistory } from 'react-router';

import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import Overlay from '@app/components/Overlay';
import ExcelValidationErrorModal from '@app/containers/Modal/ExcelValidationErrorModal';
import { ADDRESS_BOOK_ERROR_MESSAGE } from '@app/features/addressBook/constants';
import useExcelUpload, { ACCEPT_FILETYPE } from '@app/features/addressBook/hooks/useExcelUpload';
import { EXCEL_FORM_ERROR_MESSAGE } from '@app/libs/excel/addressBook';

export default function ExcelUploadDropZone() {
  const history = useHistory();

  const [errorMessage, setErrorMessage] = useState<Error | null>(null);

  const { isExcelLoading, excelValidationErrors, reset, uploadExcel, uploadError } = useExcelUpload(
    {
      onSuccess: () => {
        history.push('/settings/address-book');
      },
    }
  );

  const { isDragActive, open, getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ACCEPT_FILETYPE,
    },
    multiple: false,
    noClick: true,
    useFsAccessApi: false,
    onDropAccepted: (files: File[]) => {
      uploadExcel(files[0]);
    },
    onDropRejected: (fileRejections) => {
      if (fileRejections[0].errors[0].code === 'too-many-files') {
        setErrorMessage(new Error('한 개의 파일만 선택해 주세요.'));
      } else {
        setErrorMessage(new Error(EXCEL_FORM_ERROR_MESSAGE));
      }
    },
  });

  return (
    <>
      <Flex
        {...getRootProps()}
        alignItems="center"
        backgroundColor="boxBackgroundNeutralLight"
        borderColor={isDragActive ? 'boxBorderEmphasisBase' : 'boxBorderNeutralBase'}
        borderRadius="3px"
        borderStyle="dashed"
        borderWidth={1}
        flexDirection="column"
        height={300}
        justifyContent="center"
        position="relative"
        sx={{
          ':hover': {
            backgroundColor: 'boxBackgroundNeutralSlightlyLight',
          },
        }}
      >
        <Input {...(getInputProps() as InputProps)} data-testid="pdf-file-input" />
        {isDragActive && <DraggingOverlay />}
        <DefaultStatus onFileSelect={open} />
      </Flex>
      <ErrorModal
        error={errorMessage || uploadError}
        errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
        isOpen
        title="연락처 추가 실패"
        onClose={() => {
          if (errorMessage) {
            setErrorMessage(null);
          }
          if (uploadError) {
            reset();
          }
        }}
      />
      <ExcelValidationErrorModal
        errors={excelValidationErrors}
        isOpen={excelValidationErrors.length > 0}
        onClose={reset}
        onRetry={() => {
          reset();
          open();
        }}
      />
      {isExcelLoading && (
        <Overlay>
          <GlobalLoadingSpinner
            message={
              <>
                엑셀 파일을 업로드하는 중입니다.
                <br />
                잠시만 기다려 주세요.
              </>
            }
            overLayClassName="Overlay--active"
          />
        </Overlay>
      )}
    </>
  );
}

function DraggingOverlay() {
  return (
    <Flex
      alignItems="center"
      backgroundColor="boxBackgroundEmphasisBase"
      borderRadius="3px"
      color="textEmphasisBase"
      flexDirection="column"
      fontSize="md"
      fontWeight="medium"
      height="100%"
      justifyContent="center"
      position="absolute"
      width="100%"
    >
      여기에 파일을 놓아 주세요.
    </Flex>
  );
}

function DefaultStatus({ onFileSelect }: { onFileSelect: () => void }) {
  return (
    <>
      <Text as="p" color="textNeutralSlightlyLight" mb={6}>
        입력한 엑셀 파일을 이곳에 끌어다 놓거나
        <br />
        아래 버튼을 눌러 업로드해 주세요.{' '}
      </Text>
      <Button mb={6} variant="primary" onClick={onFileSelect}>
        엑셀 파일 업로드
        <Box as="span" ml={2}>
          <FontAwesomeIcon icon={faArrowUpFromBracket} />
        </Box>
      </Button>
      <Text as="p" color="textNeutralLight" fontSize="sm">
        <Text color="iconNeutralSlightlyDark" fontSize="sm">
          <FontAwesomeIcon icon={faCheck} />
        </Text>{' '}
        지원 파일 : XLSX
      </Text>
    </>
  );
}
